import { c as _c } from "react/compiler-runtime";
/* eslint-disable jsx-a11y/alt-text */
import Image from 'next/image';
import ChevronRightIcon from '@/assets/icons/heroIcons/Chevron-right.svg';
import { motion } from 'framer-motion';
import { normalizePath, uppercaseFirstLetter } from '@/utils/string';
import MenuPolygon from '@/assets/decorative/menuPolygon.svg';
import { Section, SubSection } from './useSections';
import { mergeClasses } from '@/utils/style';
import { Link } from '@/components/links';
import { Button } from '@/components/buttons';
import { getMediaQuery } from '@/styles/sizes';
import useMediaQuery from '@/hooks/useMediaQuery';
import { useRouter } from 'next/router';
import { navLinks } from '@/sections/layout/Header/Header';
import dynamic from 'next/dynamic';
import { PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { Suspense } from 'react';
export const DynamicDirectionIndicators = dynamic(() => import('./DirectionIndicators').then(mod => mod.DirectionIndicators), {
  ssr: false
});
export type VariantsOptions = {
  isScrolling: boolean;
  isResponsive: boolean;
  isLargeDesktop: boolean;
};
const SUBMENU_OPEN_OFFSET = 5;
const SUBMENU_CLOSED_OFFSET = 10;
const SUBMENU_POLYGON_OPEN_OFFSET = 45;
const SUBMENU_POLYGON_CLOSED_OFFSET = 80;
const SUBMENU_SCROLL_OFFSET = 32;
const SUBMENU_SCROLL_OFFSET_COMPACT = 20;
export function getSubmenuScrollOffset(isLargeDesktop: boolean): number {
  return isLargeDesktop ? SUBMENU_SCROLL_OFFSET : SUBMENU_SCROLL_OFFSET_COMPACT;
}
export const variants = {
  open: ({
    isScrolling,
    isResponsive,
    isLargeDesktop
  }: VariantsOptions) => ({
    opacity: 1,
    y: isResponsive ? SUBMENU_OPEN_OFFSET + (isScrolling ? getSubmenuScrollOffset(isLargeDesktop) : 0) : 0,
    left: isResponsive ? '5%' : 0,
    transition: {
      staggerChildren: 0.15
    }
  }),
  closed: ({
    isScrolling,
    isResponsive,
    isLargeDesktop
  }: VariantsOptions) => ({
    opacity: isResponsive ? 0 : 1,
    y: isResponsive ? SUBMENU_CLOSED_OFFSET + (isScrolling ? getSubmenuScrollOffset(isLargeDesktop) : 0) : 0,
    left: isResponsive ? '5%' : 0,
    transition: {
      staggerChildren: 0.02,
      staggerDirection: -1
    }
  })
};
const polygonVariants = {
  open: ({
    isScrolling,
    isResponsive,
    isLargeDesktop
  }: VariantsOptions) => ({
    zIndex: 10,
    opacity: 1,
    y: SUBMENU_POLYGON_OPEN_OFFSET + (isScrolling ? getSubmenuScrollOffset(isLargeDesktop) : 0),
    display: isResponsive ? 'block' : 'none',
    transition: {
      delay: 0.2
    }
  }),
  closed: ({
    isScrolling,
    isLargeDesktop
  }: VariantsOptions) => ({
    zIndex: 0,
    opacity: 0,
    y: SUBMENU_POLYGON_CLOSED_OFFSET + (isScrolling ? getSubmenuScrollOffset(isLargeDesktop) : 0),
    display: 'none',
    transition: {
      delay: -0.2
    }
  })
};
const itemVariants = {
  open: {
    opacity: 1,
    transition: {
      y: {
        stiffness: 500,
        velocity: -50
      }
    }
  },
  closed: {
    opacity: 0,
    transition: {
      y: {
        stiffness: 500
      }
    }
  }
};
function isHighlighted(sectionId: Section['id']) {
  return sectionId === 'allProducts';
}
function isProductsSection(sectionId: Section['id']) {
  return sectionId === 'products';
}
export function Subsection(t0) {
  const $ = _c(61);
  const {
    subsection,
    sectionId,
    sectionName,
    isOpen,
    handleClick,
    promo,
    isScrolling,
    isSectionActive,
    variant
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getMediaQuery("xl");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isResponsive = useMediaQuery(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getMediaQuery("3xl");
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isLargeDesktop = useMediaQuery(t2);
  const router = useRouter();
  let t3;
  if ($[2] !== sectionId) {
    t3 = isProductsSection(sectionId);
    $[2] = sectionId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const hasProductsLayout = t3;
  if (!subsection) {
    return null;
  }
  let t4;
  if ($[4] !== isScrolling || $[5] !== isSectionActive || $[6] !== variant) {
    t4 = mergeClasses(navLinks, "relative flex w-full items-center justify-between text-gray-900", {
      "xl:text-blue-500": isSectionActive
    }, {
      "xl:text-white": variant === "inverted" && !isScrolling
    });
    $[4] = isScrolling;
    $[5] = isSectionActive;
    $[6] = variant;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== sectionId) {
    t5 = uppercaseFirstLetter(sectionId);
    $[8] = sectionId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `headerSubmenu${t5}`;
  let t7;
  if ($[10] !== sectionId) {
    t7 = uppercaseFirstLetter(sectionId);
    $[10] = sectionId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = `headerSubmenu${t7}`;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = mergeClasses("text-lg font-bold whitespace-nowrap sm:font-semibold xl:mr-0.5 xl:text-[15px] xl:font-normal 2xl:text-base");
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== sectionName) {
    t10 = <h4 className={t9}>{sectionName}</h4>;
    $[13] = sectionName;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = variant === "inverted" && !isScrolling ? "xl:text-white" : undefined;
  let t12;
  if ($[15] !== isOpen || $[16] !== t11) {
    t12 = <Suspense><DynamicDirectionIndicators isOpen={isOpen} className={t11} /></Suspense>;
    $[15] = isOpen;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = isOpen ? "open" : "closed";
  let t14;
  if ($[18] !== isLargeDesktop || $[19] !== isResponsive || $[20] !== isScrolling) {
    t14 = {
      isScrolling,
      isResponsive,
      isLargeDesktop
    };
    $[18] = isLargeDesktop;
    $[19] = isResponsive;
    $[20] = isScrolling;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = t14 satisfies VariantsOptions;
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <MenuPolygon className="menu-polygon" />;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t13 || $[24] !== t15) {
    t17 = <motion.div className="menu-polygon absolute left-6 z-0 hidden xl:block" animate={t13} initial="closed" variants={polygonVariants} custom={t15}>{t16}</motion.div>;
    $[23] = t13;
    $[24] = t15;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== handleClick || $[27] !== sectionName || $[28] !== t10 || $[29] !== t12 || $[30] !== t17 || $[31] !== t4 || $[32] !== t6 || $[33] !== t8) {
    t18 = <Button onClick={handleClick} className={t4} aria-label={sectionName} variant="unstyled" analyticsParam={t6} testId={t8}>{t10}{t12}{t17}</Button>;
    $[26] = handleClick;
    $[27] = sectionName;
    $[28] = t10;
    $[29] = t12;
    $[30] = t17;
    $[31] = t4;
    $[32] = t6;
    $[33] = t8;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = isOpen ? "grid" : "hidden";
  let t20;
  if ($[35] !== t19) {
    t20 = mergeClasses("content-visibility-auto relative mt-3 mb-3 grid-cols-none grid-rows-1 gap-1 rounded-3xl", "xl:shadow-floating items-start xl:absolute xl:mt-0 xl:mb-6 xl:grid-cols-2 xl:gap-5 xl:bg-white xl:p-10", "3xl:before:-top-11 3xl:before:-right-10 3xl:before:-left-10 3xl:before:h-11 xl:before:absolute xl:before:-top-8 xl:before:-right-8 xl:before:-left-8 xl:before:h-8 xl:before:content-['']", t19);
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const t21 = isOpen ? "open" : "closed";
  let t22;
  if ($[37] !== isLargeDesktop || $[38] !== isResponsive || $[39] !== isScrolling) {
    t22 = {
      isScrolling,
      isResponsive,
      isLargeDesktop
    };
    $[37] = isLargeDesktop;
    $[38] = isResponsive;
    $[39] = isScrolling;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const t23 = t22 satisfies VariantsOptions;
  let t24;
  if ($[41] !== isSectionActive || $[42] !== router || $[43] !== subsection) {
    let t25;
    if ($[45] !== isSectionActive || $[46] !== router) {
      t25 = subsection_0 => {
        const isSubsectionActive = isSectionActive && router.pathname === normalizePath(subsection_0.path);
        return <motion.li key={subsection_0.id} className={mergeClasses("flex items-center transition-colors", isHighlighted(subsection_0.id) && "xl:mt-0")} variants={itemVariants}><Link to={subsection_0.path} analyticsParam={`headerSubmenu${uppercaseFirstLetter(subsection_0.id)}`} testId={`headerSubmenu${uppercaseFirstLetter(subsection_0.id)}`} className={mergeClasses("flex w-full items-center gap-3 rounded-lg bg-white px-4 py-3 text-gray-900 no-underline transition", "group hover:bg-blue-500 hover:text-white", "xl:w-96 xl:items-start xl:rounded-2xl xl:bg-inherit xl:p-4", isSubsectionActive && "xl:bg-blue-500 xl:text-white")} aria-label={subsection_0.name}><div className={mergeClasses("text-blue-500 group-hover:text-white", isSubsectionActive && "xl:text-white")}>{subsection_0.icon}</div><div><h5 className="text-sm font-semibold xl:mb-2 xl:text-base">{subsection_0.name}</h5><div className="hidden text-xs xl:block">{subsection_0.description}</div></div><ChevronRightIcon className="ml-auto text-blue-500 group-hover:text-white xl:hidden" /></Link></motion.li>;
      };
      $[45] = isSectionActive;
      $[46] = router;
      $[47] = t25;
    } else {
      t25 = $[47];
    }
    t24 = subsection.map(t25);
    $[41] = isSectionActive;
    $[42] = router;
    $[43] = subsection;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[48] !== hasProductsLayout || $[49] !== promo || $[50] !== sectionId) {
    t25 = promo && promo.path && <li className={mergeClasses("col-start-3 row-start-1 row-end-[2_span] hidden lg:-mt-4 lg:-mr-4 lg:-mb-4 lg:ml-16 xl:block", hasProductsLayout ? "row-end-[3_span]" : "row-end-[2_span]")}><Link analyticsParam={`headerSubmenu${uppercaseFirstLetter(sectionId)}Promo`} testId={`headerSubmenu${uppercaseFirstLetter(sectionId)}Promo`} to={promo.path} target={promo.target} className="w-[17.5rem]"><Image {...promo.image} width={280} height={300} className="rounded-2xl" /></Link></li>;
    $[48] = hasProductsLayout;
    $[49] = promo;
    $[50] = sectionId;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t20 || $[53] !== t21 || $[54] !== t23 || $[55] !== t24 || $[56] !== t25) {
    t26 = <motion.ul className={t20} animate={t21} variants={variants} initial="closed" custom={t23}>{t24}{t25}</motion.ul>;
    $[52] = t20;
    $[53] = t21;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t18 || $[59] !== t26) {
    t27 = <>{t18}{t26}</>;
    $[58] = t18;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  return t27;
}